import { get, post, del } from 'utils/network';
import IApiResponse from 'models/IApiResponse';
import { ApiRoute } from '../../utils/config';
import {
  CreateReviewParams,
  CreateReviewResponse,
  LtoItem,
  Product,
  ProductRecommendationList,
  YotpoReviewsData,
  YotpoReviewsResponse,
} from '../../models/product.types';

// Fetch product for the given productId or slug
export const fetchProduct = async (identifier: number | string): Promise<Product | undefined> => {
  const {
    ok,
    parsedBody,
  } = await get<Product>(`${ApiRoute.PRODUCTS}/${identifier}`);
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody;
};

// Fetch product recommendations for the given productId or slug
export const fetchProductRecommendations = async (identifier: number | string,
  isLoggedOut: boolean):
  Promise<ProductRecommendationList | undefined> => {
  const {
    ok,
    parsedBody,
  } = await get<ProductRecommendationList>(`${ApiRoute.PRODUCTS_V2}/${identifier}/recommendations?logged_out=${isLoggedOut}`);
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody;
};

// Fetch limited-time-offering for the given productId or slug
export const fetchLtoItem = async (identifier: number | string): Promise<LtoItem | undefined> => {
  const {
    ok,
    parsedBody,
  } = await get<LtoItem>(`${ApiRoute.LTO}/${identifier}`);
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody;
};

// Adds given product to user's favorites
export const favoriteProduct = async (skuId: number): Promise<IApiResponse | undefined> => {
  const {
    ok,
    parsedBody,
  } = await post<IApiResponse>(`${ApiRoute.FAVORITE_PRODUCT}/${skuId}`);
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody;
};

export const unFavoriteProduct = async (skuId: number): Promise<IApiResponse | undefined> => {
  const {
    ok,
  } = await del<IApiResponse>(`${ApiRoute.FAVORITE_PRODUCT}/${skuId}`);
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return { status: 'success' };
};

export const fetchBottomLine = async (
  productId: number,
): Promise<YotpoReviewsData | undefined> => {
  const {
    ok,
    parsedBody,
  } = await get<YotpoReviewsResponse>(
    `${ApiRoute.PRODUCTS}/${productId}/reviews`,
  );
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody?.response;
};

export const createProductReview = async (
  productId: number,
  params: CreateReviewParams,
): Promise<CreateReviewResponse> => {
  const {
    ok,
    parsedBody,
  } = await post<CreateReviewResponse>(
    `${ApiRoute.PRODUCTS}/${productId}/create_review`,
    {
      review_score: params.reviewScore,
      review_title: params.reviewTitle,
      review_body: params.reviewBody,
      display_name: params.displayName,
      email: params.email,
      sku_id: params.skuId,
      product_title: params.productTitle,
      product_url: params.productUrl,
      total_media_attached: params.totalMediaAttached,
      custom_fields: params.customFields,
    },
  );
  if (!ok) {
    throw new Error('Failed to submit review');
  }
  return parsedBody!;
};

export const uploadReviewImage = async (
  productId: number,
  imageUploadToken: string,
  image: File,
): Promise<void> => {
  const formData = new FormData();
  formData.append('image_upload_token', imageUploadToken);
  formData.append('image', image);

  const response = await fetch(
    `${ApiRoute.PRODUCTS}/${productId}/upload_review_image`,
    {
      method: 'POST',
      body: formData,
    },
  );
  if (!response.ok) {
    throw new Error('Failed to upload review image');
  }
};

export const fetchFilteredProductReviews = async (
  productId: number,
  perPage = 6,
  page = 1,
  scores?: number[],
  sort = 'date',
  direction = 'desc',
  pictured?: boolean,
): Promise<YotpoReviewsData | undefined> => {
  const params = new URLSearchParams({
    per_page: String(perPage),
    page: String(page),
    sort,
    direction,
  });
  if (scores?.length) params.set('scores[]', scores.join(','));
  if (pictured) params.set('pictured', 'true');
  const {
    ok,
    parsedBody,
  } = await post<YotpoReviewsResponse>(
    `${ApiRoute.PRODUCTS}/${productId}/filtered_reviews?${params.toString()}`,
  );
  if (!ok) {
    throw new Error('Network response was not ok');
  }

  return parsedBody?.response;
};
